import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Title from "../../../../components/title";
import { LoadingTable } from "../../../../components/Loading/LoadingTable";
import crmGestanteMensagensService from "../../../../services/crmGestanteMensagens";
import { PermissionsPage } from "../../../../util/permissionsPage";

const NIVEL_OPTIONS = [1, 2, 3, 4, 5, 6, 7];

export const CreateMensagem = () => {
  const history = useHistory();
  const params = useParams();
  const [isLoading, setIsLoading] = useState(false);

  const textareaRef = useRef(null);

  const {
    control,
    register,
    reset,
    watch,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      tipo: "",
      semana: "",
      nivel: "",
      mensagem: "",
    },
  });

  const tipoWatch = watch("tipo");

  const goBackList = () => history.push("/crm-gestante/mensagens/");

  const PLACEHOLDERS = [
    { label: "Nome do cliente", value: "{{nomeCliente}}" },
    { label: "Nome do funcionário", value: "{{nomeFuncionario}}" },
    { label: "Número da semana", value: "{{numeroSemana}}" },
  ];

  const inserirPlaceholder = (placeholder) => {
    const el = textareaRef.current;
    const current = getValues("mensagem") || "";
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const novo = current.slice(0, start) + placeholder + current.slice(end);
    setValue("mensagem", novo, { shouldDirty: true });
    setTimeout(() => {
      if (el) {
        const pos = start + placeholder.length;
        el.focus();
        el.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const getInfo = async () => {
    setIsLoading(true);
    try {
      const response = await crmGestanteMensagensService.buscarPorId(params.id);
      reset({
        tipo: response.data.tipo,
        semana: response.data.semana ?? "",
        nivel: response.data.nivel ?? "",
        mensagem: response.data.mensagem,
      });
    } catch (error) {
      toast.error("Mensagem não encontrada");
      goBackList();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (params?.id) {
      getInfo();
    }
  }, [params?.id]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        tipo: data.tipo,
        mensagem: data.mensagem,
        semana: data.tipo === "SEMANA" && data.semana !== "" ? Number(data.semana) : undefined,
        nivel: data.tipo === "NIVEL" && data.nivel !== "" ? Number(data.nivel) : undefined,
      };

      if (params?.id) {
        await crmGestanteMensagensService.atualizar(params.id, payload);
      } else {
        await crmGestanteMensagensService.criar(payload);
      }

      toast.success("Mensagem salva com sucesso");
      goBackList();
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      toast.error(errorMessage ?? "Erro ao salvar mensagem");
      console.log(error);
    }
  };

  if (isLoading) {
    return (
      <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="90vh">
        <LoadingTable />
      </Box>
    );
  }

  return (
    <>
      {PermissionsPage("crmGestanteMensagens") && (
      <>
      <Box ml={2}>
        <Title title={params?.id ? "Editar Mensagem CRM Gestante" : "Criar Mensagem CRM Gestante"} />
      </Box>

      <Box sx={{ px: 3, paddingBottom: 3 }}>
        <Box display="flex" gap={4} width="100%">
          <Box maxWidth="340px">
            <Typography variant="h6">
              Configure a mensagem que será exibida na fila de atendimento.
            </Typography>
            <Typography pt={2} variant="body1">
              Utilize os placeholders disponíveis para personalizar a mensagem automaticamente.
            </Typography>
            <Typography pt={2} variant="body2" color="text.secondary">
              Prioridade de resolução: <strong>SEMANA</strong> {">"} <strong>NÍVEL</strong> {">"} <strong>PADRÃO</strong> | <strong>1º CONTATO</strong> e <strong>FOLLOW-UP</strong> sempre disponíveis
            </Typography>
          </Box>

          <Paper sx={{ p: 2, flex: 1 }} variant="outlined" elevation={3}>
            <Stack spacing={2} component="form" onSubmit={handleSubmit(onSubmit)}>
              <FormControl size="small" fullWidth error={!!errors.tipo}>
                <InputLabel>Tipo</InputLabel>
                <Controller
                  name="tipo"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select {...field} label="Tipo" disabled={isSubmitting}>
                      <MenuItem value="SEMANA">SEMANA — por semana gestacional específica</MenuItem>
                      <MenuItem value="NIVEL">NÍVEL — por nível de contato (N1-N7)</MenuItem>
                      <MenuItem value="PADRAO">PADRÃO — fallback genérico</MenuItem>
                      <MenuItem value="PRIMEIRO_CONTATO">1º CONTATO — mensagem de primeiro contato</MenuItem>
                      <MenuItem value="FOLLOWUP_PRIMEIRO_CONTATO">FOLLOW-UP — follow-up do 1º contato</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>

              {tipoWatch === "SEMANA" && (
                <TextField
                  {...register("semana", {
                    required: true,
                    min: 1,
                    max: 42,
                    pattern: /^[0-9]+$/,
                  })}
                  label="Semana gestacional (1-42)"
                  type="number"
                  size="small"
                  disabled={isSubmitting}
                  error={!!errors.semana}
                  helperText="Número da semana gestacional (1 a 42)"
                  inputProps={{ min: 1, max: 42 }}
                />
              )}

              {tipoWatch === "NIVEL" && (
                <FormControl size="small" fullWidth error={!!errors.nivel}>
                  <InputLabel>Nível de contato</InputLabel>
                  <Controller
                    name="nivel"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select {...field} label="Nível de contato" disabled={isSubmitting}>
                        {NIVEL_OPTIONS.map((n) => (
                          <MenuItem key={n} value={n}>
                            N{n}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              )}

              <Box>
                <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                  {PLACEHOLDERS.map((p) => (
                    <Chip
                      key={p.value}
                      label={p.label}
                      size="small"
                      variant="outlined"
                      onClick={() => inserirPlaceholder(p.value)}
                      disabled={isSubmitting}
                      sx={{ cursor: "pointer" }}
                    />
                  ))}
                </Box>
                <TextField
                  {...register("mensagem", { required: true })}
                  label="Mensagem"
                  multiline
                  rows={5}
                  size="small"
                  fullWidth
                  disabled={isSubmitting}
                  error={!!errors.mensagem}
                  inputRef={textareaRef}
                />
              </Box>

              <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                <Button variant="outlined" disabled={isSubmitting} onClick={goBackList} type="button">
                  Voltar
                </Button>
                <Button disabled={isSubmitting} variant="contained" type="submit">
                  Salvar
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
      </>
      )}
    </>
  );
};
